import * as React from "react";
import * as Quill from "quill";
import { Static } from "@bizform/type/decorator/Static.decorator";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementDisplayMode,
    ComponentElementStatic
} from "@bizform/type/model/component_element/ComponentElement.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import { DataType } from "@bizform/type/model/model/DataType.model";
import { ComponentHelper, Debug, FunctionHelper, ObjectHelper } from "@bizform/helper/dist/js/bizflow/helper.min";
import { ComponentElementClass, ReactComponentElementInit } from "@bizform/core/dist/js/bizflow/core.min";
import { AbstractComponentElement } from "../AbstractComponentElement";
import { I18n } from "../../misc/model/I18n.model";


type Props = {
};

type WrappedCompState = {
    // attribute
    id?: string;
    // value?: string;
    placeholder?: string;
    mode?: string;
    headerTemplate?: string;

    // event

};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
};

type Delta = {
    ops: [];
};

/**
 * @since 0.0.1
 * @author Byungmin Choi
 * @class BfEditor
 * @description BfEditor component element
 */
@ComponentElementClass()
@Static<ComponentElementStatic<string>>()
export class BfEditor extends AbstractComponentElement<Props, State> {
    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_89uCMDWBTqJ4tcb0ibROCvSedygmFWH",
        category: ComponentCategory.MISC,
        label: I18n.componentElement.editor.label,
        description: I18n.componentElement.editor.description,
        icon: "",
        dataType: [DataType.STRING],
        property: null
    };

    focus(): void {
        if (this._quill) {
            this._quill.focus();
        }
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            const newState = ComponentHelper.createStateWithProperties<State, React.FormEvent<HTMLInputElement>>(
                property,
                this);
            this._propertyChanged = true;
            this.setState(newState);
        } catch (e) {
            this.emitDidCatch(e);
        }
    }

    emit(value: string): void {
    }

    emitFormState(value: string): void {
    }

    subscription(value: string): void {
        this._subscription(value, this.state.displayMode).then();
    }

    private async _subscription(value: string, mode: ComponentElementDisplayMode): Promise<void> {
        if (mode === ComponentElementDisplayMode.EDIT) {
            this._setHTML = null;
            this._divElement = null;

            while (!this._quill) {
                await FunctionHelper.timeout(100);
            }

            if (!value && value !== "") {
                if (this._editorElement && this._editorElement.children && this._editorElement.children[0]) {
                    this._editorElement.children[0].innerHTML = "";
                }
            } else
                if (value.slice(12, 23) === "ql-editor\">") {
                const innerHTMLValue = value.slice(23, value.length - 6);

                if (this.state.displayMode === ComponentElementDisplayMode.EDIT) {
                    /* EDIT 모드일 때 _innerHTML 사용, emitContents 에서 이전 값이랑 바뀌었는지 확인 */
                    if (this._quill && this._innerHTML !== innerHTMLValue) {
                        this._innerHTML = innerHTMLValue;
                        if (this._editorElement && this._editorElement.children && this._editorElement.children[0]) {
                            this._editorElement.children[0].innerHTML = innerHTMLValue;
                        }
                    }
                }
            } else if (this._quill && this._innerHTML !== value) {
                /* quill editor 의 blank 가 Emit 됐을 때 quill 에서 받는 곳 */
                this._innerHTML = value;
                if (this._editorElement && this._editorElement.children && this._editorElement.children[0]) {
                    this._editorElement.children[0].innerHTML = value;
                }
            } else {
                this.emitSubscriptionReject();
            }

        } else {
            this._innerHTML = null;
            while (!this._divElement) {
                await FunctionHelper.timeout(100);
            }
            if (!value && value !== "") {
                if (this._divElement) {
                    this._divElement.innerHTML = "";
                }
            } else if (value.slice(12, 23) === "ql-editor\">") {
                const innerHTMLValue = value.slice(23, value.length - 6);

                if (this._setHTML !== innerHTMLValue) {
                    this._setHTML = innerHTMLValue;
                    if (this._divElement) {
                        this._divElement.innerHTML = innerHTMLValue;
                    }
                }
            } else if (this._divElement && this._setHTML !== value) {
                /* quill editor 의 blank 가 Emit 됐을 때 div 에서 받는 곳 */
                this._setHTML = value;
                if (this._divElement) {
                    this._divElement.innerHTML = "";
                }
            } else {
                this.emitSubscriptionReject();
            }
        }
    }

    @ReactComponentElementInit(BfEditor)
    static init(): ComponentElement<string> {
        return;
    }

    initDone(): void {
    }

    destroy(): void {
        // proceed something to clean up
        this.destroyDone();
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    private _editorInit: boolean;
    private _propertyChanged: boolean;
    private _editor: any;
    private _quill: any;
    private _editorElement: HTMLElement;
    private _divElement: HTMLElement;
    private _innerHTML: string;
    private _tempHTML: HTMLElement;
    private _setHTML: string;

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {

            }
        };

        this._setRef = this._setRef.bind(this);
        this._emitContents = this._emitContents.bind(this);
    }

    private async _initEditor(): Promise<void> {
        this._editorInit = true;
        let toolbarContainer;
        const { mode } = this.state.wrappedCompState;
        switch (mode) {
            case "simple":
                toolbarContainer = [
                    ["bold", "italic", "underline", "strike"],        // toggled buttons
                    ["blockquote", "code-block"],

                    [{ "list": "ordered"}, { "list": "bullet" }],
                    [{ "indent": "-1"}, { "indent": "+1" }],          // outdent/indent

                    [{ "size": [] }],

                    [{ "font": [] }],
                    [{ "align": [] }],

                    ["clean"]                                         // remove formatting button
                ];
                break;
            case "full":
                toolbarContainer = [
                    ["bold", "italic", "underline", "strike"],        // toggled buttons
                    ["blockquote", "code-block"],

                    [{ "list": "ordered"}, { "list": "bullet" }],
                    [{ "script": "sub"}, { "script": "super" }],      // superscript/subscript
                    [{ "indent": "-1"}, { "indent": "+1" }],          // outdent/indent
                    [{ "direction": "rtl" }],                         // text direction

                    [{ "size": ["small", false, "large", "huge"] }],  // custom dropdown

                    [{ "color": [] }, { "background": [] }],          // dropdown with defaults from theme
                    [{ "font": [] }],
                    [{ "align": [] }],
                    ["link", "image", "video"],

                    ["clean"]                                         // remove formatting button
                ];
                break;
            default:
                toolbarContainer = [
                    ["bold", "italic", "underline", "strike"],        // toggled buttons
                    ["blockquote", "code-block"],

                    [{ "list": "ordered"}, { "list": "bullet" }],
                    [{ "indent": "-1"}, { "indent": "+1" }],          // outdent/indent

                    [{ "size": [] }],

                    [{ "font": [] }],
                    [{ "align": [] }],

                    ["clean"]                                         // remove formatting button
                ];
                break;
        }
        while (!this._editorElement || !this._editorElement.hasAttribute) {
            await FunctionHelper.timeout(100);
        }
        if ( this.state.displayMode === ComponentElementDisplayMode.EDIT && this._editorElement) {
            const dom = document.getElementById(this.elementId);
            if (dom && dom.childNodes && dom.childNodes.length > 1) {
                const toolbar = dom.querySelector(".ql-toolbar");
                if (toolbar && toolbar.parentElement) {
                    toolbar.parentElement.removeChild(toolbar);
                }
            }

            this._quill = new Quill(this._editorElement, {
                modules: {
                    toolbar: {
                        container: toolbarContainer,
                        handlers: {
                            "image": function() {
                                const range = this.quill.getSelection();
                                const value = prompt("What is the image URL");
                                if (value) {
                                    this.quill.insertEmbed(range.index, "image", value, Quill.sources.USER);
                                }
                            },
                            "video": function() {
                                const range = this.quill.getSelection();
                                const value = prompt("What is the video URL");
                                if (value) {
                                    this.quill.insertEmbed(range.index, "video", value, Quill.sources.USER);
                                }
                            }
                        }
                    }
                },
                placeholder: this.state.wrappedCompState.placeholder,
                theme: "snow"
            });
            this._quill.on("text-change", this._emitContents);
            this.readyComponent();
        }
    }

    private _emitContents() {
        if (this._editorElement.children[0].innerHTML === "") {
            this.emit("<div class=\"ql-editor\"><p><br></p></div>");
        } else if (this._innerHTML !== this._editorElement.children[0].innerHTML) {
            this._innerHTML = this._editorElement.children[0].innerHTML;

            this._tempHTML = document.createElement("div");
            this._tempHTML.appendChild(this._editorElement.children[0].cloneNode(true));

            this._tempHTML.children[0].removeAttribute("data-gramm");
            this._tempHTML.children[0].removeAttribute("contenteditable");
            this._tempHTML.children[0].removeAttribute("data-placeholder");

            this.emit(this._tempHTML.innerHTML);
            this._tempHTML = null;
        }
    }

    shouldComponentUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>, nextContext: any): boolean {
        return ObjectHelper.hasDifference(nextState, this.state);
    }

    componentWillUnmount(): void {
        if (this._quill && this._quill.root) {
            this._quill.root = null;
            this._quill = null;
        } else if (this._quill) {
            this._quill = null;
        }
        this._editorElement = null;
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.emitDidCatch(error);
    }

    emitDidCatch(error: Error | any): void {
        Debug.console.error(error);
    }

    emitSubscriptionReject(): void {
    }

    render(): React.ReactNode {
        const {
            show,
            displayMode
        } = this.state;

        if (!show) {
            return null;
        } else {
            switch (displayMode) {
                case ComponentElementDisplayMode.EDIT:
                    this._removeQuill();
                    if (this._editorInit !== true || this._propertyChanged === true) {
                        this._initEditor().then();
                    }
                    return this._render();
                case ComponentElementDisplayMode.READONLY:
                case ComponentElementDisplayMode.DISABLED:
                case ComponentElementDisplayMode.PRINT:
                    return this._render4Disable();
                default:
                    return null;
            }
        }
    }

    private _render(): React.ReactNode {
        let content = (<div ref={el => this._editorElement = el}></div>);
        return (
            <div id={this.elementId}>
                {content}
            </div>
        );
    }

    private _render4Disable(): React.ReactNode {
        this._removeQuill();
        this._editorInit = false;

        this._readyComponent().then();

        if (this._editorElement && this._editorElement.children[0] && this._editorElement.children[0].innerHTML) {
            if (this._editorElement.children[0].innerHTML === "<p><br></p>") {
                this._setHTML = "";
                this._editorElement = null;
            } else {
                this._innerHTML = this._editorElement.children[0].innerHTML;

                this._tempHTML = document.createElement("div");
                this._tempHTML.appendChild(this._editorElement.children[0].cloneNode(true));

                this._tempHTML.children[0].removeAttribute("data-gramm");
                this._tempHTML.children[0].removeAttribute("contenteditable");
                this._tempHTML.children[0].removeAttribute("data-placeholder");

                this._setHTML = this._tempHTML.innerHTML;
                this._tempHTML = null;
                this._editorElement = null;
            }
            return <div ref={el => this._divElement = el} dangerouslySetInnerHTML={{__html: this._setHTML}}></div>;
        } else {
            return <div ref={el => this._divElement = el} dangerouslySetInnerHTML={{__html: this._setHTML}}></div>;
        }
    }

    private _setRef(ref) {
        if (ref) {
            this._editor = ref;
        }
    }

    private _removeQuill() {
        if (this._quill) {
            (this._quill as any).__proto__ = null;
            this._quill = null;
        }
    }

    private async _readyComponent(): Promise<void> {
        await FunctionHelper.timeout(10);
        this.readyComponent();
    }
}
